// The sandbox conversation drawn as the user's pane draws a conversation (peerception-agent-v2 `MessageBubble`,
// `StepsList`, `Spinner`, `TurnClock`): the message on the right, then the agent's text and steps in the order they
// came, each step with its mark and its target, the activity line and the turn's clock while it runs, the time it took
// once done. Unlike the pane, steps stay once settled, each with how long it took, and the user's own time for the
// turn it played sits beside the sandbox's.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { TranscriptMessage } from '../../types/api';
import { AgentMessage } from '../quality/transcript';
import { activity, formatDuration, formatStep, turnMs, type Fidelity, type Plan, type SandboxTurn, type Step, type StepStatus } from './replay';

function Spinner() {
  return <span aria-hidden="true" className="inline-block h-3 w-3 shrink-0 animate-pc-spin rounded-full border-[1.5px] border-accent border-t-transparent align-[-2px]" />;
}

/** The add-in's step marks (`StepsList.StepDot`). */
export function StepDot({ status }: { status: StepStatus }) {
  switch (status) {
    case 'done':
      return <div className="grid h-3 w-3 shrink-0 animate-pc-pop place-items-center rounded-full bg-accent text-[8px] leading-none text-white">✓</div>;
    case 'running':
      return <Spinner />;
    case 'error':
      return <div className="grid h-3 w-3 shrink-0 animate-pc-pop place-items-center rounded-full bg-warn text-[8px] font-semibold leading-none text-white">!</div>;
    case 'cancelled':
      return <div className="grid h-3 w-3 shrink-0 animate-pc-pop place-items-center rounded-full border-[1.5px] border-line text-[9px] leading-none text-fainter">×</div>;
    default:
      return <div className="h-3 w-3 shrink-0 rounded-full border-[1.5px] border-[#d0d2ce]" />;
  }
}

function StepRow({ step }: { step: Step }) {
  const failed = step.status === 'error';
  const colour = failed ? 'text-warn' : step.status === 'running' ? 'text-ink' : 'text-muted';
  return (
    <div className="flex min-h-[22px] animate-pc-in-enter items-center gap-2 py-px text-[12px]" data-testid={`step-${step.id}`} data-status={step.status}
         title={step.error ?? JSON.stringify(step.input).slice(0, 400)}>
      <StepDot status={step.status} />
      <span className={`truncate ${colour}`}>
        {step.label}
        {failed && step.error ? <span className="text-warn"> — {step.error}</span> : null}
      </span>
      {step.target && <span className="shrink-0 font-mono text-[11px] text-faint">{step.target}</span>}
      <span className="ml-auto shrink-0 font-mono text-[10.5px] tabular-nums text-fainter">{step.ms != null ? formatStep(step.ms) : ''}</span>
    </div>
  );
}

function PlanBlock({ plan }: { plan: Plan }) {
  const { t } = useTranslation();
  return (
    <details className="rounded-md border border-line-card bg-accent-bg px-3 py-2 text-[12px]" data-testid="pane-plan">
      <summary className="cursor-pointer font-mono text-[11px] text-accent-dark">
        {t('quality.plan')}: {[plan.operation, plan.name].filter(Boolean).join(' ')}
        {typeof plan.calls === 'number' && ` · ${t('quality.planCalls', { count: plan.calls })}`}
      </summary>
      {plan.markdown && <div className="chat-md mt-1.5"><ReactMarkdown remarkPlugins={[remarkGfm]}>{plan.markdown}</ReactMarkdown></div>}
    </details>
  );
}

function FidelityLine({ f }: { f: Fidelity | null }) {
  const { t } = useTranslation();
  if (!f) return null;
  const functions = Object.keys(f.unsupported ?? {});
  if (!functions.length && !(f.loops ?? []).length) return null;
  const cells = functions.reduce((n, k) => n + (f.unsupported[k]?.length ?? 0), 0);
  return (
    <div className="rounded-sm bg-warn-bg px-2 py-1 text-[11.5px] text-warn" data-testid="fidelity">
      {functions.length > 0 && t('sandbox.fidelityFunctions', { functions: functions.join(', '), cells })}
      {functions.length > 0 && f.loops.length > 0 && ' '}
      {f.loops.length > 0 && t('sandbox.fidelityLoops', { count: f.loops.length })}
    </div>
  );
}

function TurnView({ turn, now, userMs, details }: { turn: SandboxTurn; now: number; userMs: number | null; details: TranscriptMessage[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const live = turn.state === 'running' || turn.state === 'waiting';
  const ms = turnMs(turn, now);
  const doing = activity(turn);
  return (
    <section className="flex flex-col gap-2" data-testid={`pane-turn-${turn.index}`}>
      <div className="flex flex-col items-end gap-0.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.06em] text-fainter">
          {turn.recorded != null ? t('sandbox.pane.recorded', { index: turn.recorded }) : t('sandbox.pane.typed')}
          {turn.mode ? ` · ${turn.mode}` : ''}{turn.indexVersion != null ? ` · v${turn.indexVersion}` : ''}
        </span>
        <div className="max-w-[85%] whitespace-pre-wrap wrap-break-word rounded-[10px_10px_2px_10px] bg-bubble px-3 py-2 text-[13px] text-ink">{turn.message}</div>
      </div>
      <div className="flex flex-col gap-1.5">
        {turn.plans.map((p, i) => <PlanBlock key={i} plan={p} />)}
        {turn.segments.map((seg, i) =>
          seg.kind === 'text' ? (
            <div key={i} className="chat-md"><ReactMarkdown remarkPlugins={[remarkGfm]}>{seg.text}</ReactMarkdown></div>
          ) : (
            <div key={i} className="flex flex-col">{seg.steps.map((s) => <StepRow key={s.id} step={s} />)}</div>
          ),
        )}
        {turn.error && <p className="text-[12px] text-danger">{turn.error}</p>}
        <FidelityLine f={turn.fidelity} />
        {live ? (
          <div className="flex h-[22px] items-center gap-2 font-mono text-[11px] text-muted" role="status" data-testid="activity">
            {turn.state === 'waiting' ? <span>{t('sandbox.pane.waiting')}</span> : <><Spinner /><span>{t(`sandbox.pane.${doing}`)}</span></>}
            <span className="shrink-0 tabular-nums text-fainter" data-testid="turn-clock">{formatDuration(ms)}</span>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] tabular-nums text-fainter" data-testid="turn-footer">
            <span data-testid="turn-clock">{formatDuration(ms)}</span>
            {userMs != null && <span data-testid="user-clock">{t('sandbox.pane.userTook', { time: formatDuration(userMs) })}</span>}
            {turn.committedVersion != null && <span>{t('sandbox.pane.committed', { version: turn.committedVersion })}</span>}
            {turn.state !== 'done' && <span className="text-warn">{t(`sandbox.pane.${turn.state}`)}</span>}
            {details.length > 0 && (
              <button type="button" className="underline hover:text-accent" onClick={() => setOpen((v) => !v)} data-testid={`details-${turn.index}`}>
                {t(open ? 'sandbox.pane.hideDetails' : 'sandbox.pane.details')}
              </button>
            )}
          </div>
        )}
        {open && (
          <div className="flex flex-col gap-3 rounded-md border border-line-soft bg-white p-3">
            {details.filter((m) => m.role === 'assistant').map((m, i) => <AgentMessage key={i} m={m} open={false} />)}
          </div>
        )}
      </div>
    </section>
  );
}

/** The pane: every turn of the sandbox, the clock of a running one ticking each second. `userTimes` holds the user's own
 *  time for each recorded turn (by its index), `messages` the detailed transcript a turn's details open on. */
export function SandboxPane({ turns, userTimes, messages }: { turns: SandboxTurn[]; userTimes: Map<number, number>; messages: TranscriptMessage[] }) {
  const [now, setNow] = useState(() => Date.now());
  const ticking = turns.some((x) => x.state === 'running');
  useEffect(() => {
    if (!ticking) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [ticking]);
  return (
    <div className="flex flex-col gap-5 rounded-md border border-line-card bg-pane p-4" data-testid="sandbox-pane">
      {turns.map((turn) => (
        <TurnView key={`${turn.index}-${turn.startedAt}`} turn={turn} now={now}
                  userMs={turn.recorded != null ? (userTimes.get(turn.recorded) ?? null) : null}
                  details={messages.filter((m) => m.turn_index === turn.index)} />
      ))}
    </div>
  );
}
